import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CalendarClock, CarFront, Check, Clock, Luggage, MapPin, Plus, Users } from "lucide-react";
import { api, publicMedia } from "@/api";
import { pick, useLang } from "@/i18n";
import { GoldImage } from "@/components/GoldImage";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";

const Row = ({ icon: Icon, label, value, testId }) => (
    <div className="flex items-center gap-3 border-b border-amber-500/10 py-3 last:border-0" data-testid={testId}>
        <Icon size={15} className="shrink-0 text-amber-400/90" />
        <span className="text-[10px] uppercase tracking-[0.18em] text-stone-500">{label}</span>
        <span className="ml-auto text-sm font-medium text-stone-200 text-right">{value}</span>
    </div>
);

/* Dettaglio pubblico di un transfer del gestionale: solo dati reali restituiti. */
export default function TransferDetail() {
    const { id } = useParams();
    const { lang, t } = useLang();
    const tx = t.transfers;
    const [item, setItem] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        setItem(null);
        setError(null);
        api.get(`/public/transfers/${encodeURIComponent(id)}`)
            .then((r) => setItem(r.data))
            .catch((e) => setError(e?.response?.status === 404 ? "notfound" : "unavailable"));
    }, [id]);

    if (error) {
        return (
            <div className="marble-bg min-h-screen pt-32 pb-24" data-testid="transfer-detail-error">
                <div className="max-w-3xl mx-auto px-5 text-center">
                    <p className="text-stone-300">{error === "notfound" ? tx.detailNotFound : t.common.catalogError}</p>
                    <Link to="/transfer" className="mt-6 inline-flex items-center gap-2 text-amber-300 text-sm hover:text-amber-200">
                        <ArrowLeft size={14} /> {tx.backToList}
                    </Link>
                </div>
            </div>
        );
    }
    if (!item) {
        return <div className="marble-bg min-h-screen pt-32 pb-24 text-center text-stone-500">{t.common.loading}</div>;
    }

    const name = pick(item.name, lang);
    const desc = pick(item.description, lang);
    const from = pick(item.from, lang);
    const to = pick(item.to, lang);
    const wait = pick(item.wait, lang);
    const availability = pick(item.availability, lang);
    const conditions = pick(item.conditions, lang);
    const price = item.price != null && item.price !== "" ? `€ ${Number(item.price).toLocaleString("it-IT")}` : null;

    return (
        <div className="marble-bg min-h-screen pt-28 pb-24" data-testid="transfer-detail">
            <Seo lang={lang} title={`${name} — TOURVOYAGERS`} description={desc} />
            <div className="max-w-5xl mx-auto px-5 sm:px-8">
                <Link to="/transfer" data-testid="transfer-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-amber-300/90 hover:text-amber-200">
                    <ArrowLeft size={14} /> {tx.backToList}
                </Link>

                <Reveal>
                    <div className="relative mt-6 h-64 sm:h-96 overflow-hidden rounded-2xl border border-amber-500/20">
                        <GoldImage src={publicMedia(item.image_url)} alt={name} className="absolute inset-0 h-full w-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#080909] via-[#080909]/45 to-transparent sm:from-[#080909]/90 sm:via-transparent" />
                        {price && (
                            <span className="absolute right-5 top-5 rounded-full border border-amber-400/60 bg-[#0b0b0c]/85 px-4 py-2 text-sm font-bold text-amber-300" data-testid="transfer-detail-price">
                                {price}{item.priceType ? <span className="ml-1.5 text-xs font-normal text-stone-400">{item.priceType}</span> : null}
                            </span>
                        )}
                        <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8">
                            <h1 className="font-display text-3xl sm:text-4xl text-amber-50 leading-tight" data-testid="transfer-detail-name">{name}</h1>
                            {(from || to) && (
                                <p className="mt-2 flex flex-wrap items-center gap-2 text-sm text-amber-200/85" data-testid="transfer-detail-route">
                                    <MapPin size={14} className="text-amber-400" /> {from} {to && <><ArrowRight size={13} className="text-stone-500" /> {to}</>}
                                </p>
                            )}
                        </div>
                    </div>
                </Reveal>

                <div className="mt-10 grid gap-10 lg:grid-cols-12">
                    <div className="lg:col-span-7">
                        {desc && <p className="text-base leading-relaxed text-stone-300 whitespace-pre-line" data-testid="transfer-detail-desc">{desc}</p>}

                        {item.includes?.length > 0 && (
                            <div className="mt-8">
                                <h2 className="text-[11px] uppercase tracking-[0.25em] text-amber-400/90 font-semibold">{tx.fldIncludes}</h2>
                                <ul className="mt-3 space-y-2" data-testid="transfer-detail-includes">
                                    {item.includes.map((s, i) => (
                                        <li key={i} className="flex gap-2.5 text-sm text-stone-300"><Check size={15} className="mt-0.5 shrink-0 text-amber-400" />{pick(s, lang)}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {item.supplements?.length > 0 && (
                            <div className="mt-6">
                                <h2 className="text-[11px] uppercase tracking-[0.25em] text-amber-400/90 font-semibold">{tx.fldSupplements}</h2>
                                <ul className="mt-3 space-y-2" data-testid="transfer-detail-supplements">
                                    {item.supplements.map((s, i) => (
                                        <li key={i} className="flex gap-2.5 text-sm text-stone-400"><Plus size={15} className="mt-0.5 shrink-0 text-amber-500/80" />{pick(s, lang)}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {conditions && (
                            <div className="mt-8 rounded-xl border border-amber-500/15 bg-black/30 p-5">
                                <h2 className="text-[11px] uppercase tracking-[0.25em] text-amber-400/90 font-semibold">{tx.fldConditions}</h2>
                                <p className="mt-2 text-sm text-stone-400 leading-relaxed">{conditions}</p>
                            </div>
                        )}
                    </div>

                    <div className="lg:col-span-5">
                        <div className="gold-card rounded-2xl p-6" data-testid="transfer-detail-info">
                            {item.duration && <Row icon={Clock} label={tx.fldDuration} value={pick(item.duration, lang)} testId="detail-duration" />}
                            {item.vehicle && <Row icon={CarFront} label={tx.fldVehicle} value={item.vehicle} testId="detail-vehicle" />}
                            {item.capacity != null && <Row icon={Users} label={tx.fldPassengers} value={item.capacity} testId="detail-capacity" />}
                            {item.luggage != null && <Row icon={Luggage} label={tx.fldLuggage} value={pick(item.luggage, lang)} testId="detail-luggage" />}
                            {wait && <Row icon={Clock} label={tx.fldWait} value={wait} testId="detail-wait" />}
                            {availability && <Row icon={CalendarClock} label={tx.fldAvailability} value={availability} testId="detail-availability" />}
                            {item.stops?.length > 0 && (
                                <p className="pt-3 text-xs text-stone-400">
                                    <span className="text-stone-500 uppercase tracking-widest text-[9px]">{tx.fldStops}: </span>
                                    {item.stops.map((s) => pick(s, lang)).join(" · ")}
                                </p>
                            )}
                            <Link to={`/transfer?transfer=${encodeURIComponent(item.id)}`} data-testid="transfer-detail-request"
                                className="mt-6 flex items-center justify-center gap-2 rounded-full bg-amber-400 px-8 py-3.5 text-[11px] font-bold uppercase tracking-[0.14em] text-black transition-all hover:bg-amber-300 hover:shadow-[0_0_28px_rgba(241,211,108,0.4)]">
                                {tx.request} <ArrowRight size={14} />
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
